import React, { Component } from "react";
import { Link } from "react-router-dom";
import { AxiosResponse } from "axios";
import { Modal } from "semantic-ui-react";
import { Enrolment } from "../../../../stores/EnrolmentStore/Types";
import { QuestionPreview } from "../../../../stores/CoursePreviewStore/Types";
import { postRequest } from "../../../../utils/httpRequest";

interface Props {
  answerId: number;
  enrolment: Enrolment;
  showModal: boolean;
  answerState: string;
  lesson: QuestionPreview;
  answerStateTagline: string;
  imageUrl: string;
  markerProfilePictureUrls?: string[];
  // Where Continue goes, worked out by the parent via nextLessonPath: on the
  // last lesson of the course that is the first lesson still owed rather than
  // the one that happens to come next in order. Falls back to the plain
  // sequential path for any caller that has not worked it out.
  nextPath?: string;
  handleHideAnswerStateModal: (e: any) => void;
}

type ResultKind =
  | "pass"
  | "fail"
  | "marking"
  | "cert"
  | "nolives"
  | "nolivesagain";


const fallbackMarkerImages = [
  "https://s3.amazonaws.com/42courses-user/user-avatars/1.png",
  "https://s3.amazonaws.com/42courses-user/user-avatars/2.png",
  "https://s3.amazonaws.com/42courses-user/user-avatars/3.png",
  "https://s3.amazonaws.com/42courses-user/user-avatars/4.png",
];

// A few of the fail titles promise another go. With no lives left there is
// not one, so those are swapped for a title that does not.
const TITLES_PROMISING_A_RETAKE = [
  "Round two awaits",
  "Have another crack",
  "Back to the drawing board",
  "Better luck imminently",
  "Not wrong forever",
];

class AnswerStateModal extends Component<Props> {
  useLive = (e) => {
    const { answerId, handleHideAnswerStateModal } = this.props;

    e.preventDefault();
    handleHideAnswerStateModal(e);

    postRequest(`/api/answers/${answerId}/life_uses`, {})
      .then((response: AxiosResponse) => {
        window.location.reload();
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
      });
  };

  resultKind = (): ResultKind => {
    const { enrolment, answerState } = this.props;

    if (enrolment.state === "passed") {
      return "cert";
    }

    // A wrong answer with no lives left. The course is not over: lives only
    // buy retakes, so the learner carries on and simply cannot redo this one.
    // (This used to tell them they had failed the course.) The first time it
    // is explained; after that it is the ordinary fail pop-up with no retake.
    // Whether it has been explained is kept on the learner's enrolment, so it
    // holds on any device: the server says so with each answer it marks.
    if (
      answerState === "failed" &&
      enrolment.state === "started" &&
      enrolment.lives_count < 1
    ) {
      return enrolment.out_of_lives_explained ? "nolivesagain" : "nolives";
    }

    if (answerState === "failed") {
      return "fail";
    }

    if (answerState === "submitted" || answerState === "pending") {
      return "marking";
    }

    return "pass";
  };

  resultTitle = () => {
    const { answerStateTagline } = this.props;

    switch (this.resultKind()) {
      case "cert":
        return "You did it";
      case "nolives":
        return "Out of lives, not out of the course";
      case "nolivesagain":
        return TITLES_PROMISING_A_RETAKE.includes(answerStateTagline)
          ? "Not this time"
          : answerStateTagline;
      case "marking":
        return answerStateTagline || "We will mark this for you now";
      default:
        return answerStateTagline;
    }
  };

  resultText = () => {
    switch (this.resultKind()) {
      case "fail":
        return "That one costs a life. Have another go for a single life, or dismiss and move to the next lesson.";
      case "marking":
        return "Your answer will be marked by one of our marvellous tutors, usually within 45 minutes. Keep going, you've got this.";
      case "cert":
        return "Course complete, and comfortably above the line. Your certificate is ready and waiting.";
      case "nolives":
        return "That one was wrong, and you have no lives left to retake it. Don't panic: you can still finish the course. You just can't redo the ones you get wrong from here, so maybe read the next question twice.";
      case "nolivesagain":
        return "No lives left, so no retake on this one. Onwards.";
      default:
        return "";
    }
  };

  renderMedia = () => {
    const { imageUrl, answerState, markerProfilePictureUrls } = this.props;
    const kind = this.resultKind();

    if (kind === "cert") {
      return (
        <div className="mini-cert" role="img" aria-label="Certificate preview">
          <div className="mini-cert__mark">42</div>
          <div className="mini-cert__kick">42courses</div>
          <div className="mini-cert__t">Mini MBA Certificate</div>
          <div className="mini-cert__r mini-cert__r--wide" />
          <div className="mini-cert__r mini-cert__r--short" />
          <div className="mini-cert__seal" aria-hidden="true" />
        </div>
      );
    }

    if (kind === "marking") {
      return (
        <div className="rm__gif rm__marking-gif">
          <img
            src={imageUrl}
            className={`answer-state ${answerState}`}
            alt=""
          />
        </div>
      );
    }

    if (imageUrl) {
      return (
        <div className="rm__gif">
          <img
            src={imageUrl}
            className={`answer-state ${answerState}`}
            alt=""
          />
        </div>
      );
    }

    return null;
  };

  renderActions = () => {
    const { enrolment, handleHideAnswerStateModal, answerState, lesson } =
      this.props;
    const kind = this.resultKind();

    if (kind === "cert") {
      return (
        <Link
          className="rm-btn"
          to={{ pathname: `/enrolments/${enrolment.id}/certificate` }}
        >
          Get your certificate
        </Link>
      );
    }

    if (kind === "nolives" || kind === "nolivesagain") {
      const nextPath = this.props.nextPath || lesson?.next_question_path;

      return (
        <>
          {nextPath && (
            <Link to={nextPath} className="rm-btn" onClick={handleHideAnswerStateModal}>
              Continue
            </Link>
          )}
          {(kind === "nolives" || !nextPath) && (
            <button
              className="rm-btn rm-btn--ghost"
              type="button"
              onClick={handleHideAnswerStateModal}
            >
              Close
            </button>
          )}
        </>
      );
    }

    return (
      <>
        {answerState === "failed" &&
          enrolment.state === "started" &&
          enrolment.lives_count > 0 && (
            <button className="rm-btn" type="button" onClick={this.useLive}>
              Retake for 1 life
            </button>
          )}
        {answerState === "failed" && (
          <button
            className="rm-btn rm-btn--ghost"
            type="button"
            onClick={handleHideAnswerStateModal}
          >
            Dismiss
          </button>
        )}
        {answerState !== "failed" &&
          !["passed", "failed"].includes(enrolment.state) && (
            <Link
              to={this.props.nextPath || lesson?.next_question_path}
              className="rm-btn"
              onClick={handleHideAnswerStateModal}
            >
              Continue
            </Link>
          )}
      </>
    );
  };

  render() {
    const { enrolment, showModal, handleHideAnswerStateModal, answerState } =
      this.props;
    const kind = this.resultKind();
    const media = this.renderMedia();

    return (
      <Modal
        size="tiny"
        dimmer="blurring"
        open={showModal && answerState.length > 0}
        onClose={handleHideAnswerStateModal}
        className={`answer-state-modal rm rm--${
          kind === "nolives" || kind === "nolivesagain" ? "fail" : kind
        } ${enrolment.state}`}
      >
        <Modal.Content className="rm__content">
          <button
            className="rm__close"
            type="button"
            aria-label="Close and stay on this lesson"
            onClick={handleHideAnswerStateModal}
          >
            &times;
          </button>
          <div className="rm__media">{media}</div>
          <h2 className="rm__title">{this.resultTitle()}</h2>
          <p className="rm__text">{this.resultText()}</p>
          <div className="rm__actions">{this.renderActions()}</div>
          {kind === "cert" && (
            <div className="rm-confetti" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((idx) => (
                <i key={idx} />
              ))}
            </div>
          )}
        </Modal.Content>
      </Modal>
    );
  }
}

export default AnswerStateModal;
